import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTag, updateTag } from "@/helpers/apis";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
  toast,
} from "@altimateai/lego";
import { LoadingButton, Stack } from '@altimateai/lego'
import { TAGS_QUERY_KEY } from "../useTags";
import { Tag } from "../types";
import { SettingsTracking } from "@/lib/tracking/settings";

const formSchema = z.object({
  display_name: z
    .string()
    .min(1, "Tag name is required")
    .max(100, "Tag name must be less than 100 characters"),
  description: z
    .string()
    .min(10, "Tag description must be at least 10 characters")
    .max(200, "Tag description must be less than 200 characters"),
});

type FormValues = z.infer<typeof formSchema>;

interface TagFormProps {
  tag?: Tag;
  onSubmit?: (data: Tag) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

export const TagForm = ({ tag, onSubmit, onCancel, showCancelButton = true }: TagFormProps) => {
  const queryClient = useQueryClient();
  const isEditMode = !!tag?.rk;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      display_name: tag?.display_name || "",
      description: tag?.description || "",
    },
  });

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (data: FormValues) => {
      if (isEditMode) {
        if (!tag?.rk) {
          throw new Error("Tag ID is required");
        }
        return updateTag({
          ...tag,
          display_name: data.display_name,
          description: data.description || "",
        });
      }
      return createTag({ ...data, rk: data.display_name });
    },
    onSuccess: data => {
      if (isEditMode) {
        SettingsTracking.tagEdited(tag!.rk);
      } else if (data) {
        SettingsTracking.tagCreated(data.tag_type ?? "default");
      }
      queryClient.invalidateQueries({ queryKey: [TAGS_QUERY_KEY] });
      toast({ description: `Tag ${isEditMode ? "updated" : "created"} successfully` });
      form.reset();
      if (data) {
        onSubmit?.(data);
      }
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to ${isEditMode ? "update" : "create"} tag: ${error.message || error}`,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: FormValues) => {
    mutate(data);
  };

  return (
    <Form {...form}>
      <form role="form" onSubmit={form.handleSubmit(handleSubmit)} className="al-space-y-4">
        <Stack direction="column" gap="xl">
          <FormField
            control={form.control}
            name="display_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tag Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter tag name" {...field} className="!al-m-0" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter tag description"
                    className="resize-none !al-m-0"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Stack justify="end" className="al-gap-2">
            {showCancelButton && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <LoadingButton type="submit" loading={isLoading}>
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update"
                  : "Create"}
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </Form>
  );
};
